/**
 * AppStatusBar - Bottom status bar
 *
 * Shows connection status, staleness warnings, and errors.
 */

import { Wifi, WifiOff, AlertCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getChannel, isPreviewChannel } from '@/lib/storage';
import type { StalenessLevel } from '@/lib/reconnect';

export interface AppStatusBarProps {
  status: string;
  error: string | null;
  /** Feed staleness level for banner display */
  staleness?: StalenessLevel;
  /** Seconds since last feed update */
  secondsSinceUpdate?: number | null;
}

export function AppStatusBar({ status, error, staleness, secondsSinceUpdate }: AppStatusBarProps) {
  const isConnected = status === 'Connected';
  const statusLabel = isConnected ? 'API connected' : 'API disconnected';

  const showStaleBanner = staleness === 'stale';
  const showDisconnectedBanner = staleness === 'disconnected' && isConnected;

  return (
    <div className="flex h-full items-center justify-between px-3 text-xs">
      {/* Left: Connection status */}
      <div className="flex items-center gap-2">
        {isConnected ? (
          <Wifi className="h-3 w-3 text-green-500" />
        ) : (
          <WifiOff className="text-muted-foreground h-3 w-3" />
        )}
        <span className={cn(isConnected ? 'text-green-500' : 'text-muted-foreground')}>
          {statusLabel}
        </span>
      </div>

      {/* Center: Staleness banner OR error message */}
      {showDisconnectedBanner ? (
        <div className="flex items-center gap-1.5 text-red-400">
          <AlertCircle className="h-3 w-3" />
          <span>Feed disconnected — prices may be outdated{secondsSinceUpdate != null ? ` (${secondsSinceUpdate}s ago)` : ''}</span>
        </div>
      ) : showStaleBanner ? (
        <div className="flex items-center gap-1.5 text-amber-400">
          <AlertTriangle className="h-3 w-3" />
          <span>Feed stale — no update for {secondsSinceUpdate ?? '?'}s</span>
        </div>
      ) : error ? (
        <div className="text-destructive flex items-center gap-1.5">
          <AlertCircle className="h-3 w-3" />
          <span className="max-w-md truncate">{error}</span>
        </div>
      ) : null}

      {/* Right: Channel badge + build version */}
      <div className="flex items-center gap-2">
        {isPreviewChannel() && (
          <span className="rounded bg-amber-500/20 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase text-amber-400">
            {getChannel()}
          </span>
        )}
        <span className="text-muted-foreground font-mono">{__BUILD_COMMIT__}</span>
      </div>
    </div>
  );
}
